// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/UsefulServices.tsx
// 🔧 PURPOSE: L'elenco dei servizi utili, una card con le righe (icona,
//             titolo, sottotitolo, freccia). Cambia con la vista:
//             - guest / before / after: "Info utili · Buono a sapersi":
//               Parcheggio, Farmacia, Lavanderia, Raccolta rifiuti (come
//               fare la differenziata; non in "after": l'ospite è partito),
//               Emergenze ed "Esperienze e noleggi", che apre il foglio con
//               le sei esperienze;
//             - stay: "Servizi · Tutto a portata di mano": La tua casa (clima
//               e lavatrice, solo a check-in fatto e dentro il soggiorno),
//               Lavanderia, Parcheggio, Farmacia, Raccolta rifiuti ("passiamo
//               noi a ritirare la busta", non dopo l'uscita), Emergenze;
//               sotto, la griglia delle esperienze.
//
//             Rifiuti ed emergenze aprono la loro pagina (openService). La
//             riga dei rifiuti apre il ritiro solo nel soggiorno; prima
//             dell'arrivo apre la guida alla differenziata (recycleGuide),
//             anche a chi è entrato: il ritiro lì direbbe "non disponibile".
//
//             Il foglio delle esperienze si ritrova tornando indietro da una
//             sua pagina (useHomeSheetState, ?sheet=experiences).
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Compass, Home as HomeIcon, ParkingCircle, Pill, Recycle, WashingMachine, type LucideIcon } from 'lucide-react';
import { useServiceDialogs } from './services/ServiceDialogsHost';
import { ExperiencesGrid, ExperiencesSheet } from './Experiences';
import { useHomeSheetState } from './HomeSheet';
import { RowLink, RowList, Section, SectionTitle } from './ui';

export type UsefulServicesVariant = 'guest' | 'before' | 'stay' | 'after';

export interface UsefulServicesProps {
  variant: UsefulServicesVariant;
  /**
   * Nel soggiorno: la riga "La tua casa" (clima e lavatrice). Passare
   * HomeState.homeAvailable: check-in fatto, dal giorno dell'arrivo alla
   * partenza.
   */
  homeAvailable?: boolean;
  /**
   * Nel soggiorno: il giorno della partenza oltre l'orario di check-out più
   * un'ora (HomeState.stayOver). Niente ritiro rifiuti: l'ospite è partito.
   */
  stayOver?: boolean;
  className?: string;
}

/** Il nome del foglio delle esperienze nell'indirizzo della Home (?sheet=experiences). */
export const EXPERIENCES_SHEET = 'experiences';

interface Row {
  key: string;
  icon: LucideIcon;
  title: string;
  sub: string;
  to?: string;
  onClick?: () => void;
}

const UsefulServices: React.FC<UsefulServicesProps> = ({ variant, homeAvailable = false, stayOver = false, className }) => {
  const { t } = useTranslation();
  const { openService } = useServiceDialogs();
  const stay = variant === 'stay';
  const sheet = useHomeSheetState(EXPERIENCES_SHEET, !stay);
  const titleId = `home-services-title-${variant}`;

  const rows: Row[] = [];
  if (stay && homeAvailable) {
    rows.push({ key: 'home', icon: HomeIcon, title: t('home.services.homeTitle'), sub: t('home.services.homeSub'), to: '/my-stay?open=climate' });
  }
  const laundry: Row = {
    key: 'laundry',
    icon: WashingMachine,
    title: t('services.names.laundry'),
    // Senza soggiorno davanti (ospite non entrato, o già partito) restano
    // solo le lavanderie: la lavatrice di casa è per chi ci abita.
    sub: variant === 'guest' || variant === 'after' ? t('home.services.laundryGuestSub') : t('home.services.laundrySub'),
    to: '/laundry',
  };
  const parking: Row = { key: 'parking', icon: ParkingCircle, title: t('services.names.parking'), sub: t('home.services.parkingSub'), to: '/parking' };
  const pharmacy: Row = { key: 'pharmacy', icon: Pill, title: t('services.names.pharmacy'), sub: t('home.services.pharmacySub'), to: '/pharmacy' };
  const recycle: Row = {
    key: 'recycle',
    icon: Recycle,
    title: t('home.services.recycleTitle'),
    sub: stay ? t('home.services.recycleSub') : t('home.services.recycleGuestSub'),
    onClick: () => openService(stay ? 'recycle' : 'recycleGuide'),
  };
  // Dopo l'uscita (e nella vista A) niente rifiuti: né ritiro né guida.
  const withRecycle = stay ? !stayOver : variant !== 'after';
  const emergency: Row = {
    key: 'emergency',
    icon: AlertTriangle,
    title: t('services.names.emergency'),
    sub: t('home.services.emergencySub'),
    onClick: () => openService('emergency'),
  };

  if (stay) {
    rows.push(laundry, parking, pharmacy, ...(withRecycle ? [recycle] : []), emergency);
  } else {
    rows.push(parking, pharmacy, laundry, ...(withRecycle ? [recycle] : []), emergency, {
      key: 'experiences',
      icon: Compass,
      title: t('home.services.experiencesTitle'),
      sub: t('home.services.experiencesSub'),
      onClick: sheet.show,
    });
  }

  return (
    <Section labelledBy={titleId} className={className}>
      <SectionTitle
        id={titleId}
        eyebrow={stay ? t('home.services.eyebrow') : t('home.services.guestEyebrow')}
        title={stay ? t('home.services.title') : t('home.services.guestTitle')}
      />
      <RowList>
        {rows.map((row) =>
          row.to ? (
            <RowLink key={row.key} to={row.to} icon={<row.icon />} title={row.title} sub={row.sub} />
          ) : (
            <RowLink key={row.key} onClick={row.onClick} icon={<row.icon />} title={row.title} sub={row.sub} />
          ),
        )}
      </RowList>
      {stay ? <ExperiencesGrid /> : <ExperiencesSheet open={sheet.open} onClose={sheet.close} returnTo={sheet.returnTo} />}
    </Section>
  );
};

export default UsefulServices;
